import { createContext, useCallback, useContext, useEffect, useRef, useState, type TransitionEvent } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogClose,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Icon,
  Image,
  Input,
  cn,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  ZoomableImage,
  useElementSize,
  useIsMobile,
  Video,
} from "@salestrak/ui";
import { PhoneMediaLightbox } from "#app/components/media/media-lightbox-phone";
import { useMediaDownloads } from "#app/lib/hooks/use-media-downloads";
import {
  useMediaActions,
  useMediaObjectUrl,
  useMediaStreamUrl,
} from "#app/lib/hooks/use-note-media";
import { fileIconName, mediaKind } from "#app/lib/media-kind";
import { isTextEditingTarget } from "#app/lib/utils/typing-target";
import { formatDateTime } from "#app/lib/utils/format-date";
import { TITLE_INPUT } from "#app/components/detail-view/title-input";
import type { IconName, Omittable } from "@salestrak/ui";

/** A media item the lightbox can show: image (inline), video (player), or
 * anything else (PDF/doc → an Open button). */
export type LightboxMediaItem = Omittable<{
  id: string;
  contentType: string;
  fileName?: string | null;
  name?: string | null;
  originalName?: string | null;
  details?: readonly LightboxDetail[];
}>;

export type LightboxDetail = Omittable<{
  label: string;
  value: React.ReactNode;
  onClick?: () => void;
  action?: string;
}>;

export type LightboxProjectMedia = {
  items: ReadonlyMap<string, LightboxMediaItem>;
  rename: (id: string, name: string) => void;
  canRename: (id: string) => boolean;
};

const LightboxProjectContext = createContext<LightboxProjectMedia | null>(null);

export const LightboxProjectProvider = LightboxProjectContext.Provider;

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 4;
const ZOOM_STEP_BELOW = 0.2;
const ZOOM_STEP = 0.5;
const PHOTO_RADIUS = 8;

const NAV_SIZE = 56;
const FRAME_GUTTER = 16;
const NAV_INSET = FRAME_GUTTER + 16;
const INFO_WIDTH = 320;
const INFO_SPACE = INFO_WIDTH + 12;

export type PhotoLightboxProps = Omittable<{
  /** Permission-gated media, in display order (image/video/pdf/doc). */
  media: LightboxMediaItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which photo to show first. Defaults to 0. */
  initialIndex?: number;
  /** Optional heading (field name, note context, …). */
  title?: string;
  /** Prefixes download filenames (e.g. the issue name):
   * `{prefix}-photo-2.jpg`, `{prefix}-{title}.zip`. */
  downloadPrefix?: string;
  /** The viewer stays open on the photo that slides in after a delete. */
  onRemove?: (id: string, index: number) => void;
  canRemove?: (index: number) => boolean;
  onRename?: (index: number, name: string) => void;
  canRename?: (index: number) => boolean;
}>;

/**
 * Fullscreen photo viewer — reusable for any screen holding gated photo ids
 * (custom fields, note attachments). Bytes resolve through the authenticated
 * content route.
 *
 * Base UI coordinates nested layers natively, so a lightbox opened from
 * inside the issue-view sheet stacks above it — no stacking workaround. A
 * phone gets its own presentation (`PhoneMediaLightbox`).
 */
export function MediaLightbox({
  media,
  open,
  onOpenChange,
  initialIndex = 0,
  title,
  downloadPrefix,
  onRemove,
  canRemove,
  onRename,
  canRename,
}: PhotoLightboxProps) {
  const [anchor, setIndex] = useState(0);
  const count = media.length;
  // Clamped at read so a shrinking list lands on the photo that took its place.
  const index = Math.min(anchor, Math.max(count - 1, 0));

  const [realSize, setRealSize] = useState(false);
  useEffect(() => {
    if (!open) return;
    setIndex(Math.max(initialIndex, 0));
    setRealSize(false);
  }, [open, initialIndex]);

  // Clamps rather than wraps: the edge arrows disable at the ends.
  const step = useCallback(
    (delta: number) => {
      setIndex((current) => Math.min(Math.max(Math.min(current, count - 1) + delta, 0), count - 1));
    },
    [count],
  );

  const current = media[index] ?? null;
  const currentId = current?.id ?? null;
  // Zoom belongs to the photo it was set on; paging away resets to fit.
  const [zoomed, setZoomed] = useState<{ id: string | null; zoom: number }>({ id: null, zoom: 1 });
  const zoom = zoomed.id === currentId ? zoomed.zoom : 1;
  const setZoom = useCallback((next: number) => setZoomed({ id: currentId, zoom: next }), [currentId]);
  const zoomStep = useCallback(
    (dir: 1 | -1) => {
      const size = (dir < 0 ? zoom <= 1 : zoom < 1) ? ZOOM_STEP_BELOW : ZOOM_STEP;
      const next = Math.round((Math.round(zoom / size) + dir) * size * 10) / 10;
      setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next)));
    },
    [setZoom, zoom],
  );
  const zoomable = !!current && mediaKind(current.contentType) === "image";

  // A key typed into a field (the rename) belongs to the field.
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTextEditingTarget(event.target)) return;
      if (event.key === "ArrowLeft") step(-1);
      else if (event.key === "ArrowRight") step(1);
      else if (!zoomable) return;
      else if (event.key === "=" || event.key === "+") zoomStep(1);
      else if (event.key === "-") zoomStep(-1);
      else if (event.key === "0") setZoom(1);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, setZoom, step, zoomStep, zoomable]);

  // Focus lands on the stage, not the name input, so opening never seats a caret in the name.
  const stageRef = useRef<HTMLDivElement>(null);
  const project = useContext(LightboxProjectContext);
  const heading = current?.name || (current && project?.items.get(current.id)?.name) || title;
  const renameAt =
    onRename ??
    (project
      ? (at: number, name: string) => {
          const id = media[at]?.id;
          if (id) project.rename(id, name);
        }
      : undefined);
  const canRenameAt = onRename ? canRename : (at: number) => !!media[at] && !!project?.canRename(media[at].id);
  const removeCurrent =
    onRemove && current && (canRemove?.(index) ?? true)
      ? () => onRemove(current.id, index)
      : undefined;

  const [infoOpen, setInfoOpen] = useState(false);
  const [infoMounted, setInfoMounted] = useState(false);
  if (infoOpen && !infoMounted) setInfoMounted(true);
  const { ref: viewRef, size: viewSize } = useElementSize<HTMLDivElement>();
  const photoWidth = viewSize.width > 0 ? viewSize.width - 2 * FRAME_GUTTER : undefined;

  const phone = useIsMobile();
  const { downloadPhoto, downloadAllPhotos, downloadingAll } =
    useMediaDownloads(downloadPrefix);

  const handleDownload = useCallback(async () => {
    if (!current) return;
    await downloadPhoto(current.id, index + 1, current.fileName);
  }, [downloadPhoto, current, index]);

  const handleDownloadAll = useCallback(async () => {
    await downloadAllPhotos(
      media.map((m) => ({ id: m.id, fileName: m.fileName })),
      title,
    );
  }, [downloadAllPhotos, media, title]);

  if (phone) {
    return (
      <PhoneMediaLightbox
        downloadingAll={downloadingAll}
        index={index}
        media={media}
        onDownload={() => void handleDownload()}
        onDownloadAll={() => void handleDownloadAll()}
        onIndexChange={setIndex}
        onOpenChange={onOpenChange}
        onRemove={removeCurrent}
        open={open}
        renderItem={(item) => <LightboxBody item={item} />}
        title={heading}
      />
    );
  }

  return (
    // Not responsive: fullscreen media.
    <Dialog responsive={false} open={open} onOpenChange={onOpenChange}>
      <DialogPopup size="full" surface="sunken" showCloseButton={false} initialFocus={stageRef}>
        <DialogHeader className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 py-3 ps-5 pe-3">
          {renameAt && current && canRenameAt?.(index) ? (
            // Keyed per item and name so a half-typed name never follows the arrows onto the next photo.
            <Box minWidth={0}>
              <DialogTitle visuallyHidden>{heading}</DialogTitle>
              <Input
                key={`${current.id}:${heading ?? ""}`}
                defaultValue={heading ?? ""}
                aria-label="Name"
                placeholder="Name"
                unstyled
                className={cn(TITLE_INPUT, "text-base")}
                onBlur={(event) => {
                  const next = (event.target as HTMLInputElement).value;
                  if (next.trim() !== (heading ?? "")) renameAt(index, next);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") (event.target as HTMLInputElement).blur();
                }}
              />
            </Box>
          ) : (
            <DialogTitle className="min-w-0 truncate text-base">{heading ?? "Photos"}</DialogTitle>
          )}
          <LightboxToolbar
            zoom={
              zoomable
                ? {
                    value: zoom,
                    onStep: zoomStep,
                    onFit: () => setZoom(1),
                    realSize,
                    onRealSize: () => {
                      setRealSize((was) => !was);
                      setZoom(1);
                    },
                  }
                : null
            }
            hasCurrent={!!currentId}
            infoOpen={infoOpen}
            onInfoToggle={() => setInfoOpen((was) => !was)}
            onDownload={handleDownload}
            onRemove={removeCurrent}
          />
          <Box direction="row" justify="end">
            <DialogClose aria-label="Close" render={<Button variant="ghost" size="icon-sm" />}>
              <Icon name="x" />
            </DialogClose>
          </Box>
        </DialogHeader>
        <Box
          ref={viewRef}
          direction="row"
          fill="flex"
          minHeight={0}
          position="relative"
          overflow="hidden"
          className="isolate rounded-b-[inherit]"
        >
          {zoomable && currentId ? <LightboxBackdrop photoId={currentId} /> : null}
          <Box ref={stageRef} tabIndex={-1} direction="column" fill="flex" minWidth={0} className="outline-none">
            <Box direction="row" fill="flex" minHeight={0} position="relative" group="pane">
              {count > 1 ? (
                <LightboxNavButton dir="prev" disabled={index === 0} onClick={() => step(-1)} />
              ) : null}
              {/* The image box must STRETCH to the body height — centering the
                  column would collapse it (and the image's 100% sizing with it). */}
              <Box direction="column" fill="flex" minWidth={0} paddingX="md" paddingBottom="md">
                <Box fill="flex" minHeight={0} overflow="hidden" justify="center" rounded="lg">
                  {current ? (
                    <Box direction="column" justify="center" fill="flex" minHeight={0} width={photoWidth} shrink={false}>
                      <LightboxBody
                        item={current}
                        zoom={{ value: zoom, onChange: setZoom, realSize }}
                      />
                    </Box>
                  ) : (
                    <Box align="center">
                      <Text variant="muted">No media</Text>
                    </Box>
                  )}
                </Box>
              </Box>
              {count > 1 ? (
                <LightboxNavButton
                  dir="next"
                  disabled={index >= count - 1}
                  onClick={() => step(1)}
                />
              ) : null}
            </Box>
          </Box>
          <Box
            direction="row"
            justify="end"
            shrink={false}
            overflow="hidden"
            width={infoOpen ? INFO_SPACE : 0}
            className="transition-[width] duration-300 ease-out motion-reduce:transition-none"
            onTransitionEnd={(event: TransitionEvent) => {
              if (event.target === event.currentTarget && !infoOpen) setInfoMounted(false);
            }}
          >
            {infoMounted && current ? (
              <LightboxInfoPanel item={current} shown={infoOpen} onLeave={() => onOpenChange(false)} />
            ) : null}
          </Box>
        </Box>
      </DialogPopup>
    </Dialog>
  );
}

function ToolbarButton({
  label,
  icon,
  onClick,
  disabled,
  pressed,
}: Omittable<{
  label: string;
  icon: IconName;
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
}>) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant={pressed ? "solid" : "ghost"}
            color="neutral"
            size="icon-sm"
            disabled={disabled}
            onClick={onClick}
            aria-label={label}
            aria-pressed={pressed}
          />
        }
      >
        <Icon name={icon} size="sm" />
      </TooltipTrigger>
      <TooltipPopup side="bottom">{label}</TooltipPopup>
    </Tooltip>
  );
}

function LightboxToolbar({
  zoom,
  hasCurrent,
  infoOpen,
  onInfoToggle,
  onDownload,
  onRemove,
}: Omittable<{
  zoom: {
    value: number;
    onStep: (dir: 1 | -1) => void;
    onFit: () => void;
    realSize: boolean;
    onRealSize: () => void;
  } | null;
  hasCurrent: boolean;
  infoOpen: boolean;
  onInfoToggle: () => void;
  onDownload: () => void;
  onRemove?: () => void;
}>) {
  const divider = <Box width={1} height={16} background="border" marginX="xs" />;
  return (
    <Box
      direction="row"
      align="center"
      gap="2xs"
      padding="xs"
      background="elevated"
      rounded="full"
      border
    >
      {zoom ? (
        <>
          <ToolbarButton
            label="Real size of image"
            icon="maximize"
            pressed={zoom.realSize}
            onClick={zoom.onRealSize}
          />
          <ToolbarButton
            label="Zoom out"
            icon="minus"
            disabled={zoom.value <= ZOOM_MIN}
            onClick={() => zoom.onStep(-1)}
          />
          <Button variant="ghost" size="sm" disabled={zoom.value === 1} onClick={zoom.onFit} aria-label="Back to 100%">
            <Text size="sm" variant="muted" tabular>
              {Math.round(zoom.value * 100)}%
            </Text>
          </Button>
          <ToolbarButton
            label="Zoom in"
            icon="plus"
            disabled={zoom.value >= ZOOM_MAX}
            onClick={() => zoom.onStep(1)}
          />
          {divider}
        </>
      ) : null}
      <ToolbarButton label="Info" icon="info" disabled={!hasCurrent} pressed={infoOpen} onClick={onInfoToggle} />
      {divider}
      <ToolbarButton label="Download" icon="download" disabled={!hasCurrent} onClick={onDownload} />
      {onRemove ? <ToolbarButton label="Delete photo" icon="trash" onClick={onRemove} /> : null}
    </Box>
  );
}

function LightboxNavButton({
  dir,
  disabled,
  onClick,
}: {
  dir: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  const prev = dir === "prev";
  if (disabled) return null;
  return (
    <Box
      as="button"
      type="button"
      aria-label={prev ? "Previous photo" : "Next photo"}
      onClick={onClick}
      position="absolute"
      left={prev ? NAV_INSET : undefined}
      right={prev ? undefined : NAV_INSET}
      zIndex={1}
      width={NAV_SIZE}
      height={NAV_SIZE}
      rounded="full"
      align="center"
      justify="center"
      cursor="pointer"
      className="top-1/2 -translate-y-1/2 bg-black/40 text-white opacity-20 shadow-lg ring-1 ring-white/15 backdrop-blur-md transition-[opacity,background-color,scale] duration-150 hover:bg-black/60 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/pane:opacity-100 motion-safe:active:scale-95"
    >
      <Icon name={prev ? "chevron-left" : "chevron-right"} size="xl" />
    </Box>
  );
}

function LightboxBackdrop({ photoId }: { photoId: string }) {
  const [layers, setLayers] = useState([{ photoId, key: 0 }]);
  const top = layers[layers.length - 1];
  if (top && top.photoId !== photoId) setLayers([top, { photoId, key: top.key + 1 }]);
  return (
    <Box
      position="absolute"
      overflow="hidden"
      aria-hidden
      className="pointer-events-none inset-0 -z-10 [@media(prefers-reduced-transparency:reduce)]:hidden"
    >
      {layers.map((layer) => (
        <BackdropLayer key={layer.key} photoId={layer.photoId} />
      ))}
    </Box>
  );
}

// Own compositor layer: keeps the zoomed photo from repainting the blur in tiles, which left vertical seams.
function BackdropLayer({ photoId }: { photoId: string }) {
  const uri = useMediaObjectUrl(photoId);
  const [loaded, setLoaded] = useState(false);
  if (!uri) return null;
  return (
    <Image
      src={uri}
      alt=""
      fill
      fit="cover"
      onLoad={() => setLoaded(true)}
      className={cn(
        "absolute inset-0 scale-125 blur-3xl brightness-[.3] saturate-150 will-change-transform transition-opacity duration-500 ease-out",
        loaded ? "opacity-100" : "opacity-0",
      )}
    />
  );
}

function LightboxInfoPanel({
  item,
  shown,
  onLeave,
}: {
  item: LightboxMediaItem;
  shown: boolean;
  onLeave: () => void;
}) {
  const facts = useMediaFacts(item.id);
  const known = useContext(LightboxProjectContext)?.items.get(item.id);
  const originalName = item.originalName ?? known?.originalName ?? item.fileName;
  const name = item.name || known?.name || originalName;
  const summary = [facts?.type, facts?.dimensions, facts?.size].filter(Boolean).join(" · ");
  const groups: LightboxDetail[][] = [
    [...(item.details ?? known?.details ?? [])],
    originalName ? [{ label: "File name", value: originalName }] : [],
    [
      ...(facts?.taken ? [{ label: "Taken", value: facts.taken }] : []),
      ...(facts?.camera ? [{ label: "Camera", value: facts.camera }] : []),
      ...(facts?.location ? [{ label: "Location", value: facts.location }] : []),
    ],
  ];
  return (
    <Box
      width={INFO_WIDTH}
      shrink={false}
      margin="base"
      marginLeft="none"
      className={
        shown ? "animate-in fade-in-0 duration-300" : "opacity-0 transition-opacity duration-300"
      }
      direction="column"
      gap="lg"
      padding="lg"
      background="elevated"
      rounded="xl"
      border
      overflow="auto"
    >
      <Box direction="column" gap="2xs" minWidth={0}>
        <Text size="sm" weight="semibold" truncate={2}>
          {name ?? "Photo"}
        </Text>
        {summary ? (
          <Text size="xs" variant="muted">
            {summary}
          </Text>
        ) : null}
      </Box>
      {groups
        .filter((rows) => rows.length > 0)
        .map((rows) => (
          <Box key={rows[0]?.label} direction="column" border="top">
            {rows.map((row, i) => (
              <Box
                key={row.label}
                direction="row"
                align="center"
                justify="between"
                gap="base"
                minHeight={40}
                paddingY="sm"
                border={i > 0 ? "top" : undefined}
              >
                <Box shrink={false}>
                  <Text size="sm" variant="muted">
                    {row.label}
                  </Text>
                </Box>
                <Box direction="row" justify="end" minWidth={0}>
                  {row.onClick ? (
                    <Box
                      as="button"
                      type="button"
                      aria-label={row.action}
                      onClick={() => {
                        onLeave();
                        row.onClick?.();
                      }}
                      direction="row"
                      align="center"
                      gap="2xs"
                      minWidth={0}
                      rounded="sm"
                      className="cursor-pointer underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Text size="sm" truncate={1}>
                        {row.value}
                      </Text>
                      <Icon name="chevron-right" size="xs" color="muted" />
                    </Box>
                  ) : typeof row.value === "string" ? (
                    <Text size="sm" align="right" truncate={1}>
                      {row.value}
                    </Text>
                  ) : (
                    row.value
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        ))}
    </Box>
  );
}

type MediaFacts = {
  type: string | null;
  size: string | null;
  dimensions: string | null;
  taken: string | null;
  camera: string | null;
  location: string | null;
};

type Exif = {
  DateTimeOriginal?: Date;
  Make?: string;
  Model?: string;
  latitude?: number;
  longitude?: number;
};

function useMediaFacts(photoId: string): MediaFacts | null {
  const uri = useMediaObjectUrl(photoId);
  const [facts, setFacts] = useState<{ uri: string; facts: MediaFacts } | null>(null);
  useEffect(() => {
    if (!uri) return;
    let live = true;
    void (async () => {
      const blob = await fetch(uri).then((res) => res.blob()).catch(() => null);
      const dimensions = blob?.type.startsWith("image/")
        ? await new Promise<string | null>((resolve) => {
            const img = new window.Image();
            img.onload = () => resolve(`${img.naturalWidth} × ${img.naturalHeight}`);
            img.onerror = () => resolve(null);
            img.src = uri;
          })
        : null;
      // Loaded on first use so the EXIF parser rides its own chunk.
      const exif: Exif | null = blob?.type.startsWith("image/")
        ? await import("exifr")
            .then((exifr) => exifr.parse(blob, { pick: ["DateTimeOriginal", "Make", "Model"], gps: true }))
            .catch(() => null)
        : null;
      if (!live) return;
      const { DateTimeOriginal: date, Make: make, Model: model, latitude, longitude } = exif ?? {};
      setFacts({
        uri,
        facts: {
          type: blob?.type ? blob.type.replace(/^\w+\//, "").toUpperCase() : null,
          size: blob ? formatBytes(blob.size) : null,
          dimensions,
          taken: date instanceof Date ? formatDateTime(date.toISOString()) : null,
          // Some models repeat their make ("Canon EOS R6"), so the make joins only once.
          camera: model ? (make && !model.startsWith(make) ? `${make} ${model}` : model) : make ?? null,
          location:
            typeof latitude === "number" && typeof longitude === "number"
              ? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
              : null,
        },
      });
    })();
    return () => {
      live = false;
    };
  }, [uri]);
  return facts && facts.uri === uri ? facts.facts : null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type LightboxZoom = {
  value: number;
  onChange: (zoom: number) => void;
  realSize: boolean;
};

function LightboxBody({ item, zoom }: Omittable<{ item: LightboxMediaItem; zoom?: LightboxZoom }>) {
  const kind = mediaKind(item.contentType);
  if (kind === "image") return <LightboxImage photoId={item.id} zoom={zoom} />;
  if (kind === "video")
    return <LightboxVideo photoId={item.id} fileName={item.fileName} />;
  return <LightboxOpen item={item} />;
}

function LightboxImage({ photoId, zoom }: Omittable<{ photoId: string; zoom?: LightboxZoom }>) {
  const uri = useMediaObjectUrl(photoId);
  if (!uri) return null;
  if (!zoom) return <Image src={uri} fill fit="contain" alt="Photo" />;
  return (
    <ZoomableImage
      src={uri}
      alt="Photo"
      zoom={zoom.value}
      onZoomChange={zoom.onChange}
      maxZoom={ZOOM_MAX}
      minZoom={ZOOM_MIN}
      radius={PHOTO_RADIUS}
      fit={zoom.realSize ? "cover" : "contain"}
    />
  );
}

function LightboxVideo({
  photoId,
  fileName,
}: Omittable<{
  photoId: string;
  fileName?: string | null;
}>) {
  const uri = useMediaStreamUrl(photoId);
  if (!uri) return null;
  return <Video src={uri} fill aria-label={fileName ?? "Video"} />;
}

function LightboxOpen({ item }: { item: LightboxMediaItem }) {
  const { openMedia } = useMediaActions();
  return (
    <Box fill="flex" align="center" justify="center" gap="md" padding="lg">
      <Icon name={fileIconName(item.contentType)} size="2xl" color="muted" />
      {item.fileName ? (
        <Text variant="muted" align="center">
          {item.fileName}
        </Text>
      ) : null}
      <Button onClick={() => openMedia(item.id)}>
        <Icon name="external-link" size="sm" />
        Open
      </Button>
    </Box>
  );
}
